import {
  ArrowLeft,
  Check,
  FileAudio,
  FileText,
  GitCompareArrows,
  PenLine,
  Plus,
  Sparkles,
  UserCheck,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import {
  ApiError,
  type EntityDossier,
  type PersonDossier,
  type PersonRelation,
} from "@/lib/api";
import { BeliefsEditor } from "@/components/BeliefEditor";
import { AddRelation, AssertedByYou } from "@/components/RelationsPanel";
import { relationLabel } from "@/lib/relationTypes";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import { categoryLabel, categoryMeta } from "@/lib/insightCategory";
import { personColour, personInitials } from "@/lib/personAvatar";
import { VERDICT_COLOUR } from "@/lib/verdicts";

/**
 * UI-8 — one person's dossier: everything the organization knows about them, gathered from
 * the recordings they spoke in, the approved insights they appear in, the relations sources
 * stated about them, and the names they go by.
 *
 * Two things are worth saying about what is on screen.
 *
 * **The paragraph at the top.** A human's own description wins over the generated summary,
 * always. Everything else in this system gates a machine's output behind a human's approval;
 * a dossier that overwrote the one sentence the owner wrote themselves would be the single
 * place it does not. Editing writes the description, so an edit is a decision that sticks.
 *
 * **The Relations panel renders whatever exists:** INS-5's source-scoped candidates and
 * WORLD-6's cross-source derivations. A person with none shows the section's empty state
 * rather than the section being absent, so the panel fills in place when synthesis writes
 * into it.
 *
 * **The owner's hand (UI-20).** The dossier takes a belief and a relation directly, written as
 * the owner's own and marked so: the Beliefs section is the person's World view page, and the
 * Relations section keeps UI-8's rows and gains WORLD-10's add form beneath them.
 */
export function PersonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { api, refreshPeople, refreshEntities } = useApp();

  const [person, setPerson] = useState<PersonDossier | null>(null);
  const [page, setPage] = useState<EntityDossier | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    // Person → person keeps this page mounted; without a reset the previous dossier shows
    // until the new read lands.
    setPerson(null);
    setPage(null);
    setProblem(null);

    void api
      .getPerson(id)
      .then((dossier) => !cancelled && setPerson(dossier))
      .catch((cause: unknown) => {
        if (cancelled) return;
        setProblem(cause instanceof ApiError ? cause.message : "The dossier could not be read.");
      });

    // WORLD-9 — the person's World view page, for the Beliefs section. A separate read that
    // may fail on its own: a dossier without its page is still a dossier.
    void Promise.resolve()
      .then(() => api.getEntityDossier(id))
      .then((dossier) => !cancelled && setPage(dossier))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const reload = useCallback(async () => {
    if (!id) return;
    setPerson(await api.getPerson(id));
    try {
      setPage(await api.getEntityDossier(id));
    } catch {
      // The page is extra; the dossier stands without it.
    }
    // The gallery card carries the same role and counts; the entity manager carries the
    // same name and aliases.
    refreshPeople();
    refreshEntities();
  }, [api, id, refreshPeople, refreshEntities]);

  if (problem) {
    return (
      <Frame>
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      </Frame>
    );
  }

  if (!person) {
    return (
      <Frame>
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      </Frame>
    );
  }

  return (
    <Frame>
      <Link
        to="/people"
        className="inline-flex w-fit items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        People
      </Link>

      <Header person={person} onChanged={reload} />
      <SummaryCard person={person} onChanged={reload} />
      <Stats person={person} />
      <Aliases person={person} onChanged={reload} />
      <Relations person={person} onChanged={reload} />
      {page && (
        <BeliefsEditor
          slug={page.slug}
          sections={page.page?.sections ?? []}
          hint="What is held about this person — from sources, or typed in by you."
          empty="Nothing held yet. Add what you know — a role, a responsibility, since when — or let sources fill it in."
          onAssert={async (input) => {
            await api.assertBelief(person.id, input);
            await reload();
          }}
        />
      )}
      <Insights person={person} />
      <Recordings person={person} />
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex max-w-[1120px] flex-col gap-[var(--space-4)] px-[var(--space-8)] py-[var(--space-8)]">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- header

function Header({ person, onChanged }: { person: PersonDossier; onChanged: () => Promise<void> }) {
  const { api } = useApp();
  const [editingRole, setEditingRole] = useState(false);

  return (
    <div className="flex items-center gap-[var(--space-4)]">
      <span
        aria-hidden
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-[length:var(--text-xl)] font-bold text-[var(--text-inverse)]"
        style={{ background: personColour(person.id) }}
      >
        {personInitials(person.name)}
      </span>

      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="text-[length:var(--text-2xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            {person.name}
          </h1>
          {person.isMe && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--vl-life)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold text-[var(--text-inverse)]">
              <UserCheck size={12} strokeWidth={2.5} />
              This is you
            </span>
          )}
        </div>

        {editingRole ? (
          <InlineText
            value={person.role ?? ""}
            placeholder="Product, Engineering, or the customer they work for"
            label="Role"
            onCancel={() => setEditingRole(false)}
            onSave={async (role) => {
              await api.setPersonRole(person.id, role);
              setEditingRole(false);
              await onChanged();
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingRole(true)}
            aria-label="Edit role"
            className="text-[length:var(--text-sm)] text-[var(--text-muted)] hover:text-[var(--vl-bridge-soft)]"
          >
            {person.role ?? "Add a role"}
          </button>
        )}
      </div>

      {/* SPCH-7. Everything personal later resolves through this link, which is why it is
          in the header on the dossier as well as on the entity detail — the dossier is the
          screen somebody actually opens looking for a person. */}
      {!person.isMe && (
        <div className="ml-auto flex items-center gap-2.5">
          <span className="max-w-[24ch] text-[length:var(--text-xs)] text-[var(--text-muted)]">
            Everything personal — commitments, prep, "my" perspective — resolves to whoever is marked as you.
          </span>
          <button
            type="button"
            onClick={async () => {
              await api.setOwner(person.id);
              await onChanged();
            }}
            className="inline-flex h-8 shrink-0 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <UserCheck size={14} strokeWidth={2} />
            This is me
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- the paragraph

/**
 * The generated summary, the human's description, and the one place they meet.
 *
 * The paragraph is written by a `summarize_person` step, so it arrives when a claimant takes
 * it — the local `Reasoner` on the GPU PC or the cloud one on the Server, decided by
 * placement. That is why this card polls rather than waits: opening a dossier never blocks on
 * a model, and a summary being written is a state the reader can see.
 *
 * A dossier with nothing written yet asks for one on open, because an empty box is not a
 * dossier. One that is merely *out of date* does not: the reader can see the paragraph that
 * exists and press Update if they want a new one, which keeps a click-through of the gallery
 * after a batch of approvals from queueing a job per person nobody asked for.
 */
function SummaryCard({ person, onChanged }: { person: PersonDossier; onChanged: () => Promise<void> }) {
  const { api } = useApp();
  const [editing, setEditing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const asked = useRef(false);

  const working = person.summaryJob === "queued" || person.summaryJob === "running";

  const ask = useCallback(async () => {
    setProblem(null);
    try {
      await api.refreshPersonSummary(person.id);
      await onChanged();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The summary could not be asked for.");
    }
  }, [api, person.id, onChanged]);

  // Nothing written and nothing in flight: ask once. `asked` rather than a dependency on the
  // job state, so a job that fails is not re-queued forever by the page that is watching it.
  useEffect(() => {
    if (asked.current || person.summary || working || person.insightCount === 0) return;

    asked.current = true;
    void ask();
  }, [person.summary, person.insightCount, working, ask]);

  // While a claimant has it, ask the Server again every few seconds. Cheap — it is the same
  // read the page already does — and it stops the moment the paragraph lands.
  useEffect(() => {
    if (!working) return;

    const timer = setInterval(() => void onChanged(), 3000);
    return () => clearInterval(timer);
  }, [working, onChanged]);

  const shown = person.description ?? person.summary;

  if (editing) {
    return (
      <Card>
        <InlineParagraph
          value={person.description ?? person.summary ?? ""}
          onCancel={() => setEditing(false)}
          onSave={async (description) => {
            await api.updateEntity(person.id, { name: person.name, description });
            setEditing(false);
            await onChanged();
          }}
        />
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start gap-[var(--space-4)]">
        <div className="min-w-0 flex-1">
          {shown ? (
            <p className="leading-relaxed text-[var(--text-secondary)]">{shown}</p>
          ) : working ? (
            <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
              Writing a summary… it arrives when the Reasoner picks this up.
            </p>
          ) : (
            <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
              {person.insightCount === 0
                ? "Nothing is known about this person yet. A summary is written once approved insights mention them."
                : person.summaryJob === "failed"
                  ? "The last attempt at a summary failed. Ask again, or check Settings → Engines & models."
                  : "No summary yet."}
            </p>
          )}

          {person.description && person.summary && (
            <p className="mt-2 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
              Your description is shown instead of the generated summary.
            </p>
          )}

          {problem && (
            <p role="alert" className="mt-2 text-[length:var(--text-xs)] text-[var(--warning)]">
              {problem}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {!working && (person.summaryJob === "failed" || (person.summaryStale && person.summary)) && (
            <button
              type="button"
              onClick={() => void ask()}
              className="inline-flex items-center gap-1.5 rounded-full border border-transparent px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--vl-bridge-soft)] hover:border-[var(--border-subtle)]"
            >
              <Sparkles size={12} strokeWidth={2} />
              {person.summaryJob === "failed" ? "Try again" : "Out of date — update"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Edit description"
            className="inline-flex items-center gap-1.5 rounded-full border border-transparent px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--text-muted)] opacity-60 transition hover:border-[var(--border-subtle)] hover:text-[var(--vl-bridge-soft)] hover:opacity-100"
          >
            <PenLine size={12} strokeWidth={2} />
            Edit
          </button>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- stats

function Stats({ person }: { person: PersonDossier }) {
  return (
    <div className="flex gap-[var(--space-6)]">
      <Stat value={String(person.meetingCount)} label="Meetings" />
      <Stat value={String(person.insightCount)} label="Insights" />
      <Stat value={person.firstSeenOn ? formatDate(person.firstSeenOn) : "—"} label="First seen" />
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex-1 rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)] text-center">
      <div className="text-[length:var(--text-2xl)] font-extrabold text-[var(--text-primary)]">{value}</div>
      <div className="text-[length:var(--text-xs)] text-[var(--text-muted)]">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------- aliases

function Aliases({ person, onChanged }: { person: PersonDossier; onChanged: () => Promise<void> }) {
  const { api } = useApp();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  async function set(aliases: string[]) {
    await api.setEntityAliases(person.id, aliases);
    await onChanged();
  }

  return (
    <Section title="Aliases" hint="Other names and spellings that resolve to this person.">
      <div className="flex flex-wrap items-center gap-1.5">
        {person.aliases.map((alias) => (
          <span
            key={alias}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--secondary)] py-0.5 pl-2.5 pr-1 text-[length:var(--text-xs)] text-[var(--secondary-foreground)]"
          >
            {alias}
            <button
              type="button"
              aria-label={`Remove ${alias}`}
              onClick={() => void set(person.aliases.filter((other) => other !== alias))}
              className="flex h-4 w-4 items-center justify-center rounded-full text-[var(--text-muted)] opacity-60 hover:text-[var(--destructive)] hover:opacity-100"
            >
              <X size={12} strokeWidth={2} />
            </button>
          </span>
        ))}

        {adding ? (
          <input
            autoFocus
            value={draft}
            aria-label="Alias"
            placeholder="Alias, then Enter"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
              setAdding(false);
              setDraft("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setAdding(false);
                setDraft("");
              }
              if (event.key !== "Enter" || draft.trim().length === 0) return;
              void set([...person.aliases, draft.trim()]);
              setAdding(false);
              setDraft("");
            }}
            className="w-[150px] rounded-full border border-[var(--primary)] bg-[var(--surface-2)] px-2.5 py-0.5 text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[var(--border-default)] px-2.5 py-0.5 text-[length:var(--text-xs)] text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--vl-bridge-soft)]"
          >
            <Plus size={12} strokeWidth={2} />
            Add alias
          </button>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------- relations

function Relations({ person, onChanged }: { person: PersonDossier; onChanged: () => Promise<void> }) {
  const { entities } = useApp();
  const live = person.relations.filter((relation) => !relation.removed);
  const removed = person.relations.filter((relation) => relation.removed);

  return (
    <Section title="Relations" hint="Stated by sources, drawn by you, under the same verdict vocabulary as beliefs.">
      <div className="flex flex-col gap-1.5">
        {live.length === 0 && removed.length === 0 && (
          <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
            No relations yet. Sources state them as they are read, and later synthesis derives them across sources.
          </p>
        )}

        {live.map((relation) => (
          <RelationRow key={relation.id} relation={relation} onChanged={onChanged} />
        ))}

        {removed.map((relation) => (
          <UndoBand key={relation.id} relation={relation} onChanged={onChanged} />
        ))}

        <AddRelation
          thisEntityId={person.id}
          thisEntityName={person.name}
          thisEntityKind="person"
          candidates={entities}
          onChanged={onChanged}
        />
      </div>
    </Section>
  );
}

function RelationRow({ relation, onChanged }: { relation: PersonRelation; onChanged: () => Promise<void> }) {
  const { api } = useApp();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative flex items-center gap-2.5 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-2.5 text-[length:var(--text-sm)] hover:border-[var(--border-default)]">
      <span aria-hidden className="shrink-0 text-[var(--vl-virtual-soft)]">
        <GitCompareArrows size={12} strokeWidth={2} />
      </span>

      <span className="min-w-0 flex-1 text-[var(--text-primary)]">
        {relation.subject} {relationLabel(relation.relationType)} {relation.object}
      </span>

      {relation.assertedByUser && <AssertedByYou />}

      <span
        className="shrink-0 rounded-full border px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold"
        style={{ borderColor: VERDICT_COLOUR[relation.verdict], color: VERDICT_COLOUR[relation.verdict] }}
      >
        {relation.verdict}
      </span>

      <span className="relative inline-flex shrink-0">
        <button
          type="button"
          onClick={() => setOpen((was) => !was)}
          title="Show the sources behind this"
          className="border-b border-dotted border-[var(--border-strong)] text-[length:var(--text-2xs)] text-[var(--text-muted)] hover:text-[var(--vl-bridge-soft)]"
        >
          {relation.sources.length} {relation.sources.length === 1 ? "source" : "sources"}
        </button>

        {open && (
          <div className="absolute right-0 top-[calc(100%+8px)] z-[70] flex w-[320px] flex-col gap-0.5 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-3)] p-2 text-left shadow-[var(--shadow-lg)]">
            <div className="px-2 pb-1.5 pt-0.5 text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              Sources
            </div>
            {relation.sources.length === 0 ? (
              <p className="px-2 pb-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">
                The insight this came from is no longer approved.
              </p>
            ) : (
              relation.sources.map((source) => (
                <Link
                  key={source.insightId}
                  to={`/recordings/${source.sourceMaterialId}?insight=${source.insightId}`}
                  className="flex items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-[length:var(--text-xs)] hover:bg-[var(--surface-2)]"
                >
                  <SourceIcon kind={source.sourceKind} />
                  <span className="min-w-0 flex-1 truncate text-[var(--vl-bridge-soft)]">
                    {source.sourceTitle ?? "Untitled source"}
                  </span>
                </Link>
              ))
            )}
          </div>
        )}
      </span>

      <button
        type="button"
        aria-label="Remove relation"
        onClick={async () => {
          await api.removeRelation(relation.id);
          await onChanged();
        }}
        className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[var(--radius-sm)] border border-transparent text-[var(--text-muted)] opacity-50 hover:border-[var(--border-subtle)] hover:text-[var(--destructive)] hover:opacity-100"
      >
        <X size={12} strokeWidth={2} />
      </button>
    </div>
  );
}

/** A dismissed relation, and the way back. It is a mark rather than a delete, so this band
 * is still here after a reload and the undo restores the row that was removed. */
function UndoBand({ relation, onChanged }: { relation: PersonRelation; onChanged: () => Promise<void> }) {
  const { api } = useApp();

  return (
    <div className="flex items-center gap-2.5 rounded-[var(--radius-md)] border border-dashed border-[var(--border-default)] px-3.5 py-2 text-[length:var(--text-xs)] text-[var(--text-muted)]">
      <span className="min-w-0 flex-1 truncate">
        Removed — {relation.subject} {relationLabel(relation.relationType)} {relation.object}
      </span>
      <button
        type="button"
        onClick={async () => {
          await api.restoreRelation(relation.id);
          await onChanged();
        }}
        className="text-[length:var(--text-xs)] text-[var(--vl-bridge-soft)] hover:underline hover:underline-offset-[3px]"
      >
        Undo
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- insights and recordings

function Insights({ person }: { person: PersonDossier }) {
  const hint =
    person.insightCount === 0
      ? "Approved insights this person appears in."
      : person.insights.length < person.insightCount
        ? `Showing ${person.insights.length} of ${person.insightCount} — approved insights this person appears in.`
        : `${person.insightCount} approved ${person.insightCount === 1 ? "insight" : "insights"} this person appears in.`;

  return (
    <Section title="Insights" hint={hint}>
      <div className="flex flex-col gap-1.5">
        {person.insights.length === 0 ? (
          <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
            Nothing approved yet. Insights arrive here once you accept them in the review queue.
          </p>
        ) : (
          person.insights.map((insight) => (
            <div
              key={insight.id}
              className="flex items-center gap-2.5 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-2.5 text-[length:var(--text-sm)] hover:border-[var(--border-default)]"
            >
              <span
                className="shrink-0 rounded-full px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold text-[var(--text-inverse)]"
                style={{ background: categoryMeta(insight.category).colour }}
              >
                {categoryLabel(insight.category)}
              </span>
              <span className="min-w-0 flex-1 text-[var(--text-primary)]">{insight.text}</span>
              <Link
                to={`/recordings/${insight.sourceMaterialId}?insight=${insight.id}`}
                className="shrink-0 border-b border-dotted border-[var(--border-strong)] text-[length:var(--text-2xs)] text-[var(--text-muted)] hover:text-[var(--vl-bridge-soft)]"
              >
                1 source
              </Link>
            </div>
          ))
        )}
      </div>
    </Section>
  );
}

function Recordings({ person }: { person: PersonDossier }) {
  return (
    <Section title="Recordings">
      <div className="flex flex-col gap-1.5">
        {person.recordings.length === 0 ? (
          <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
            Not heard in anything yet. A recording lands here once you confirm this person spoke in it.
          </p>
        ) : (
          person.recordings.map((recording) => (
            <Link
              key={recording.sourceMaterialId}
              to={`/recordings/${recording.sourceMaterialId}`}
              className="flex items-center justify-between rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-2.5 text-[length:var(--text-sm)] transition hover:border-[var(--border-strong)] hover:shadow-[var(--shadow-md)]"
            >
              <span className="text-[var(--vl-bridge-soft)]">{recording.title ?? "Untitled recording"}</span>
              <span className="text-[var(--text-muted)]">{formatDate(recording.occurredOn)}</span>
            </Link>
          ))
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------- small pieces

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-2.5">
        <h2 className="font-semibold text-[var(--text-primary)]">{title}</h2>
        {hint && <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)]">
      {children}
    </div>
  );
}

function SourceIcon({ kind }: { kind: string }) {
  const Icon = kind === "recording" ? FileAudio : FileText;
  return <Icon size={12} strokeWidth={2} aria-hidden className="shrink-0 text-[var(--text-muted)]" />;
}

function InlineText({
  value,
  label,
  placeholder,
  onSave,
  onCancel,
}: {
  value: string;
  label: string;
  placeholder?: string;
  onSave: (value: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(value);

  return (
    <input
      autoFocus
      value={draft}
      aria-label={label}
      placeholder={placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Escape") onCancel();
        if (event.key === "Enter") void onSave(draft.trim());
      }}
      onBlur={() => void onSave(draft.trim())}
      className="mt-0.5 h-7 w-[320px] rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-2 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
    />
  );
}

function InlineParagraph({
  value,
  onSave,
  onCancel,
}: {
  value: string;
  onSave: (value: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);

  return (
    <div className="flex flex-col gap-2.5">
      <textarea
        autoFocus
        rows={3}
        value={draft}
        aria-label="Description"
        onChange={(event) => setDraft(event.target.value)}
        className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-3 py-2.5 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
      />
      <div className="flex items-center gap-2">
        <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          What you write here is shown instead of the generated summary, and is not overwritten by it.
        </span>
        <span className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className={cn(
              "h-8 rounded-[var(--radius-md)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)]",
              "hover:text-[var(--text-primary)]",
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await onSave(draft.trim());
            }}
            className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
          >
            <Check size={14} strokeWidth={2.5} />
            Save
          </button>
        </span>
      </div>
    </div>
  );
}
